// Authored by Puneet Kaura
// Event inventory and instrumentation status: docs/posthog_event_plan.html

import posthog from 'posthog-js';

const POSTHOG_KEY = 'phc_AXSwGrpphX3GBMP8zwbE28SbfK87GpKJdBmmKKfAcksq';
const POSTHOG_HOST = 'https://us.i.posthog.com';
const environment = process.env.NODE_ENV || 'production';

type PostHogUser = {
  email?: string;
  id?: number | string;
  user_type?: number | string;
  geo?: string;
  designation?: string;
  plan?: number | string;
};

let hasInitialized = false;
let lastIdentifiedDistinctId: string | null = null;

const isBrowser = () => typeof window !== 'undefined';

const normalizeAnalyticsValue = (value?: string | number | null): string | number => {
  if (value === null || value === undefined) return 'none';
  if (typeof value === 'string' && value.trim() === '') return 'none';
  return value;
};

// PostHog should only run against the production frontend environment.
export const isPosthogEnabled = () => {
  if (!isBrowser()) return false;
  return environment === 'production';
};

const buildTraits = (user: PostHogUser) => {
  const traits: Record<string, string | number> = {};

  if (user.email) {
    traits.email = user.email;
    const [, emailDomain] = user.email.split('@');
    if (emailDomain) {
      traits.email_domain = emailDomain;
    }
  }

  if (user.id !== undefined && user.id !== null) {
    traits.user_django_db_id = user.id;
  }

  if (user.user_type !== undefined && user.user_type !== null) {
    traits.user_type = user.user_type;
  }

  traits.geo = normalizeAnalyticsValue(user.geo);
  traits.designation = normalizeAnalyticsValue(user.designation);
  traits.plan = normalizeAnalyticsValue(user.plan);

  return traits;
};

// PostHog is intentionally initialized behind a defensive wrapper. Analytics
// must never block app boot, routing, auth, or data fetching.
export const initPosthog = () => {
  if (!isPosthogEnabled() || hasInitialized) return;

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      defaults: '2026-01-30',
      person_profiles: 'identified_only',
    });
    hasInitialized = true;
  } catch (error) {
    console.error('PostHog init failed', error);
  }
};

// We only identify authenticated users. Anonymous traffic is still allowed to
// remain anonymous in PostHog; once auth succeeds we identify as early as
// possible from the auth flow and dedupe repeat calls for the same user.
export const identifyUser = (input?: PostHogUser | { user?: PostHogUser } | null) => {
  if (!isPosthogEnabled()) return;

  const user = input && 'user' in input ? input.user : input;
  if (!user?.email) return;

  const distinctId = user.email;
  if (lastIdentifiedDistinctId === distinctId) return;

  try {
    initPosthog();
    posthog.identify(distinctId, buildTraits(user));
    lastIdentifiedDistinctId = distinctId;
  } catch (error) {
    console.error('PostHog identify failed', error);
  }
};

// Single entry point for all event captures. Fail-open: a PostHog error must
// never propagate to the caller. Only fires in production environments.
export const captureEvent = (event: string, properties?: Record<string, unknown>) => {
  if (!isPosthogEnabled()) return;
  try {
    posthog.capture(event, properties);
  } catch (error) {
    console.error('PostHog capture failed', error);
  }
};

// Reset identity on logout or forced session clear so later events on the same
// device are not associated with the previous authenticated user.
export const resetPosthog = () => {
  lastIdentifiedDistinctId = null;

  if (!isPosthogEnabled()) return;

  try {
    initPosthog();
    posthog.reset();
  } catch (error) {
    console.error('PostHog reset failed', error);
  }
};
